import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, CreditCard, ExternalLink, RefreshCw } from "lucide-react";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) =>
  `€${Number(n || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}`;

// ── helpers ────────────────────────────────────────────────────────────────

function bookingIsAwaitingPayment(b) {
  const ps = (b.accounting?.payment_status || b.payment_status || "").toLowerCase();
  return ["pending", "unpaid", "awaiting_payment"].includes(ps);
}

function visaQuoteIsAwaitingPayment(vq) {
  const ps     = (vq.payment_status || "").toLowerCase();
  const status = (vq.status || "").toLowerCase();
  if (status === "cancelled") return false;
  if (ps === "paid") return false;
  if (ps === "awaiting_payment" || ps === "pay_later") return true;
  if (vq.invoice_generated || vq.work_started) return true;
  return false;
}

const PAYMENT_STATUS_COLORS = {
  pending:         "bg-amber-100 text-amber-800",
  unpaid:          "bg-amber-100 text-amber-800",
  awaiting_payment:"bg-amber-100 text-amber-800",
  pay_later:       "bg-blue-100 text-blue-700",
  not_requested:   "bg-slate-100 text-slate-500",
};

const SOURCE_COLORS = {
  "Flight Booking": "bg-violet-100 text-violet-700",
  "Visa Quote":     "bg-blue-100 text-blue-700",
};

// ── normalise records into a flat shape ───────────────────────────────────

function bookingToItem(b) {
  const p   = (b.passengers || [{}])[0];
  const name = `${p.first_name || ""} ${p.last_name || ""}`.trim() || "—";
  const ref  = b.accounting?.invoice_number || b.id?.slice(0, 8);
  const ps   = (b.accounting?.payment_status || b.payment_status || "pending").toLowerCase();
  return {
    id:            b.id,
    sourceType:    "Flight Booking",
    ref,
    customer:      name,
    email:         p.email || "",
    amount:        parseFloat(b.selling_price || 0),
    paymentStatus: ps,
    date:          b.created_at,
    destination:   `${b.origin || ""}→${b.destination || ""}`,
    go:            `/bookings/${b.id}`,
  };
}

function visaQuoteToItem(vq) {
  const ref    = vq.quote_number || vq.accounting?.invoice_number || vq.id?.slice(0, 8);
  const amount = parseFloat(vq.accounting?.invoice_total || vq.service_fee || 0);
  const ps     = (vq.payment_status || "not_requested").toLowerCase();
  const dest   = [vq.destination_country, vq.visa_type].filter(Boolean).join(" · ");
  return {
    id:            vq.id,
    sourceType:    "Visa Quote",
    ref,
    customer:      vq.customer_name || "—",
    email:         vq.customer_email || "",
    amount,
    paymentStatus: ps,
    date:          vq.created_at,
    destination:   dest,
    go:            vq.visa_request_id
                     ? `/visa-requests/${vq.visa_request_id}/quote`
                     : `/visa-quotes`,
  };
}

// ── component ─────────────────────────────────────────────────────────────

export default function PaymentsDue() {
  const navigate    = useNavigate();
  const { getAuthHeaders } = useAuth();

  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const h = { headers: getAuthHeaders() };
      const [bkRes, vqRes] = await Promise.allSettled([
        axios.get(`${API}/bookings`,     h),
        axios.get(`${API}/visa-quotes`,  h),
      ]);

      const bookings    = bkRes.status === "fulfilled" ? bkRes.value.data || [] : [];
      const visaQuotes  = vqRes.status === "fulfilled" ? vqRes.value.data || [] : [];

      const rows = [
        ...bookings.filter(bookingIsAwaitingPayment).map(bookingToItem),
        ...visaQuotes.filter(visaQuoteIsAwaitingPayment).map(visaQuoteToItem),
      ];

      // Sort: highest amount first
      rows.sort((a, b) => b.amount - a.amount);
      setItems(rows);
    } catch (err) {
      console.error("Failed to load payments due:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = items.filter(item => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      item.ref?.toLowerCase().includes(s) ||
      item.customer?.toLowerCase().includes(s) ||
      item.email?.toLowerCase().includes(s) ||
      item.destination?.toLowerCase().includes(s)
    );
  });

  const totalOutstanding = filtered.reduce((sum, i) => sum + i.amount, 0);

  return (
    <DashboardLayout>
      <div className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl font-bold text-slate-900">Awaiting Payment</h1>
            <p className="text-slate-600 mt-1">
              All outstanding customer payments — flight bookings and visa quotes
            </p>
          </div>
          <Button onClick={load} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Summary strip */}
        {!loading && items.length > 0 && (
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <CreditCard className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-semibold text-amber-800">
                {items.length} outstanding · {fmt(items.reduce((s, i) => s + i.amount, 0))} total
              </span>
            </div>
            {["Flight Booking", "Visa Quote"].map(type => {
              const grp = items.filter(i => i.sourceType === type);
              if (!grp.length) return null;
              return (
                <div key={type} className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-4 py-3">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${SOURCE_COLORS[type]}`}>{type}</span>
                  <span className="text-sm text-slate-600">
                    {grp.length} · {fmt(grp.reduce((s, i) => s + i.amount, 0))}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Search */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by reference, customer, email, or destination..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-400 transition"
              />
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-7 h-7 text-emerald-500" />
                </div>
                <p className="text-slate-700 font-semibold">
                  {search ? "No matching payments" : "All payments collected"}
                </p>
                <p className="text-sm text-slate-400 mt-1">
                  {search ? "Try a different search term" : "Nothing is currently awaiting payment"}
                </p>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Reference</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Details</TableHead>
                      <TableHead>Amount Due</TableHead>
                      <TableHead>Payment Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Open</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map(item => (
                      <TableRow
                        key={item.id}
                        className="cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => navigate(item.go)}
                      >
                        <TableCell>
                          <span className="font-mono font-bold text-purple-700">
                            {item.ref}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`text-xs font-bold px-2 py-1 rounded ${SOURCE_COLORS[item.sourceType] || "bg-slate-100 text-slate-600"}`}>
                            {item.sourceType}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-slate-900">{item.customer}</p>
                            {item.email && (
                              <p className="text-xs text-slate-400">{item.email}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-500">{item.destination || "—"}</span>
                        </TableCell>
                        <TableCell>
                          <span className="font-mono font-semibold text-slate-800">
                            {fmt(item.amount)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge className={PAYMENT_STATUS_COLORS[item.paymentStatus] || "bg-slate-100 text-slate-500"}>
                            {item.paymentStatus.replace(/_/g, " ")}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-500">
                            {item.date
                              ? format(new Date(item.date), "MMM d, yyyy")
                              : "—"}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={e => { e.stopPropagation(); navigate(item.go); }}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {search && filtered.length < items.length && (
                  <div className="px-4 py-3 border-t border-slate-100 text-xs text-slate-400">
                    Showing {filtered.length} of {items.length} items · {fmt(totalOutstanding)} filtered total
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
